import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Car, Paperclip, MapPin, Calendar, DollarSign, Fuel, Shield } from "lucide-react";
import { CarReservation } from "@/hooks/useTrips";
import { EditCarDialog } from "../dialogs/EditCarDialog";
import { DeleteItemDialog } from "../dialogs/DeleteItemDialog";
import { AttachmentsSheet, AttachmentsBadge, SourceEmailBadge } from "../attachments";
import { getCompanyLogoUrl } from "@/lib/companyLogos";
import { safeFormat, formatTimeOnly } from "@/lib/formatDateTime";

interface CarCardProps {
  car: CarReservation;
  tripId?: string;
  index?: number;
  readOnly?: boolean;
}

export const CarCard = ({ car, tripId, index, readOnly = false }: CarCardProps) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return "N/A";
    const result = safeFormat(dateStr, "MMM d, yyyy");
    return result === "—" ? dateStr : result;
  };

  const formatTime = (dateStr: string | undefined) => {
    if (!dateStr) return "N/A";
    const result = formatTimeOnly(dateStr);
    return result === "—" ? dateStr : result;
  };

  // Calculate rental days
  const rentalDays = car.pickup_time && car.dropoff_time
    ? Math.ceil((new Date(car.dropoff_time).getTime() - new Date(car.pickup_time).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  // Get rental company logo URL
  const logoUrl = getCompanyLogoUrl(car.rental_company, 64);

  // Check if there are expandable details to show
  // Only count fields that aren't already shown above the fold
  const hasExpandableDetails = !!(
    car.insurance ||
    car.fuel_policy ||
    car.mileage
  );

  return (
    <>
      <Card variant="interactive">
        <CardContent className="p-6">
          {/* Header with company info and actions */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <div className="w-12 h-12 rounded bg-gray-50 border border-gray-200 flex items-center justify-center overflow-hidden">
                  <img
                    src={logoUrl}
                    alt={car.rental_company}
                    className="w-8 h-8 object-contain mix-blend-multiply"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.innerHTML = `<span class="text-xs font-bold text-gray-600">${car.rental_company?.substring(0, 2).toUpperCase() || 'CA'}</span>`;
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="w-12 h-12 rounded bg-gray-50 border border-gray-200 flex items-center justify-center">
                  <span className="text-xs font-bold text-gray-600">{car.rental_company?.substring(0, 2).toUpperCase() || 'CA'}</span>
                </div>
              )}
              <div>
                <h3 className="font-semibold text-lg mb-1">{car.rental_company}</h3>
                {car.car_type && (
                  <p className="text-sm text-muted-foreground">{car.car_type}</p>
                )}
              </div>
            </div>
            {!readOnly && tripId !== undefined && index !== undefined && (
              <div className="flex items-center gap-2">
                <AttachmentsBadge
                  tripId={tripId}
                  itemType="cars"
                  itemIndex={index}
                  onClick={() => setShowAttachments(true)}
                />
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {car.source_email_id && (
                    <SourceEmailBadge onClick={() => setShowAttachments(true)} />
                  )}
                  <Button variant="ghost" size="icon" onClick={() => setShowAttachments(true)} title="Attachments">
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setShowEdit(true)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setShowDelete(true)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Pickup/Dropoff - 2 Column Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Pick-up</div>
              <div className="font-semibold">{formatDate(car.pickup_time)}</div>
              <div className="text-sm text-muted-foreground">{formatTime(car.pickup_time)}</div>
              {car.pickup_location && (
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {car.pickup_location}
                </div>
              )}
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Drop-off</div>
              <div className="font-semibold">{formatDate(car.dropoff_time)}</div>
              <div className="text-sm text-muted-foreground">{formatTime(car.dropoff_time)}</div>
              {car.dropoff_location && (
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {car.dropoff_location}
                </div>
              )}
            </div>
          </div>

          {/* Quick Info */}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
            {rentalDays > 0 && (
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {rentalDays} day{rentalDays !== 1 ? 's' : ''}
              </div>
            )}
            {car.confirmation_number && (
              <div>
                <span className="font-medium">Conf:</span> {car.confirmation_number}
              </div>
            )}
          </div>

          {/* Pricing */}
          {car.total_cost && (
            <div className="bg-gradient-to-r from-gray-50 to-gray-50/50 rounded-lg p-4 mb-4 border border-gray-200">
              {car.daily_rate && (
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Daily rate:</span>
                  <span className="font-semibold">${car.daily_rate.toLocaleString()}</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2 mt-2 flex items-center justify-between">
                <span className="font-semibold flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  Total Cost:
                </span>
                <span className="text-xl font-bold">${car.total_cost.toLocaleString()} {car.currency || 'USD'}</span>
              </div>
            </div>
          )}

          {/* Expandable Details */}
          {expanded && (
            <div className="border-t border-border pt-4 space-y-3">
              {car.insurance && (
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <span className="text-sm font-medium">Insurance:</span>
                    <p className="text-sm text-muted-foreground">{car.insurance}</p>
                  </div>
                </div>
              )}
              {car.fuel_policy && (
                <div className="flex items-start gap-2">
                  <Fuel className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <span className="text-sm font-medium">Fuel Policy:</span>
                    <p className="text-sm text-muted-foreground">{car.fuel_policy}</p>
                  </div>
                </div>
              )}
              {car.mileage && (
                <div>
                  <span className="text-sm font-medium">Mileage:</span>
                  <span className="text-sm text-muted-foreground ml-2">{car.mileage}</span>
                </div>
              )}
              {car.pickup_location && (
                <div>
                  <span className="text-sm font-medium">Pickup Location:</span>
                  <p className="text-sm text-muted-foreground mt-1">{car.pickup_location}</p>
                </div>
              )}
              {car.dropoff_location && car.dropoff_location !== car.pickup_location && (
                <div>
                  <span className="text-sm font-medium">Dropoff Location:</span>
                  <p className="text-sm text-muted-foreground mt-1">{car.dropoff_location}</p>
                </div>
              )}
            </div>
          )}

          {/* Toggle Button - only show if there are details to expand */}
          {hasExpandableDetails && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-4 text-sm text-accent hover:text-accent/80 transition-colors"
            >
              {expanded ? 'Show less' : 'Show more details'}
            </button>
          )}
        </CardContent>
      </Card>

      {!readOnly && tripId !== undefined && index !== undefined && (
        <>
          <EditCarDialog
            car={car}
            tripId={tripId}
            index={index}
            open={showEdit}
            onOpenChange={setShowEdit}
          />
          <DeleteItemDialog
            tripId={tripId}
            itemType="cars"
            index={index}
            itemName={car.rental_company}
            open={showDelete}
            onOpenChange={setShowDelete}
          />
          <AttachmentsSheet
            open={showAttachments}
            onOpenChange={setShowAttachments}
            tripId={tripId}
            itemType="cars"
            itemIndex={index}
            itemTitle={car.rental_company}
            sourceEmailId={car.source_email_id}
          />
        </>
      )}
    </>
  );
};
